import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { RootState } from '@next/core-lib/app';
import { SummaryCardAction } from '@next/core-lib/components';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EditMode, FarmResource } from 'src/app/flock/models/flock.model';

import {
  deleteFarmResourceWithConfirmationRequest,
  loadFarmResourcesRequest,
  upsertFarmResourceRequest,
} from 'src/app/flock/store/actions/flock.actions';

import {
  getFarmResources,
  getFlockResources,
  getFlocks,
} from 'src/app/flock/store/selectors/flock.selectors';

import { FarmResourcesFlockDataSource } from './datasources/farm-resources-flock.datasource';
import { FarmResourcesDataSource } from './datasources/farm-resources.datasource';
import { ResourceEditComponent } from '../resource-edit/resource-edit.component';

const FARM_RESOURCES_COLUMNS = [
  'year',
  'water',
  'electricity',
  'electricityPhotovoltaic',
  'electricityWind',
  'naturalGas',
  'diesel',
  'gasoline',
  'propaneLpg',
  'coal',
  'actions',
];

const DISPLAYED_COLUMNS = [
  'flockName',
  'flockStartDate',
  'flockEndDate',
  'water',
  'electricity',
  'electricityPhotovoltaic',
  'electricityWind',
  'naturalGas',
  'diesel',
  'gasoline',
  'propaneLpg',
  'coal',
  'status',
];

@Component({
  selector: 'app-farm-resources',
  templateUrl: './farm-resources.component.html',
  styleUrls: ['./farm-resources.component.scss'],
  standalone: false,
})
export class FarmResourcesComponent implements OnInit, OnDestroy {
  @ViewChild('resourceColumn') resourceColumn: TemplateRef<unknown>;

  private readonly destroyed$ = new Subject<void>();
  readonly farmResourcesDataSource: FarmResourcesDataSource;
  readonly farmResourcesFlockDataSource: FarmResourcesFlockDataSource;

  farmResourcesColumns: string[] = FARM_RESOURCES_COLUMNS;
  displayedColumns: string[] = DISPLAYED_COLUMNS;

  summaryCardActions: SummaryCardAction[] = [
    {
      id: '',
    },
  ];

  constructor(
    private readonly store: Store<RootState>,
    private readonly dialogService: DialogService,
    private readonly locale: LocaleService,
  ) {
    const farmResources$ = this.store.select(getFarmResources);
    const flocks$ = this.store.select(getFlocks);
    const flockResources$ = this.store.select(getFlockResources);

    this.farmResourcesDataSource = new FarmResourcesDataSource(farmResources$);
    this.farmResourcesFlockDataSource = new FarmResourcesFlockDataSource(
      locale,
      farmResources$,
      flocks$,
      flockResources$,
    );
  }

  ngOnInit(): void {
    this.store.dispatch(loadFarmResourcesRequest());
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  openResourceSlideIn(resourceRow: FarmResource = null) {
    const editMode = resourceRow !== null ? EditMode.Edit : EditMode.Add;

    this.dialogService
      .fullscreenDialog(ResourceEditComponent, {
        confirmButton: true,
        confirmButtonText: this.locale.translate(editMode),
        pushHistoryState: false,
        mode: editMode,
        resourceRow,
      })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ result }) => {
        if (result != null) {
          this.saveResource(result.resourceRow);
        }
      });
  }

  saveResource(resourceRow: FarmResource) {
    this.store.dispatch(upsertFarmResourceRequest({ request: resourceRow }));
  }

  deleteResource(resourceRow: FarmResource) {
    this.store.dispatch(deleteFarmResourceWithConfirmationRequest({ id: resourceRow.id }));
  }
}
